import { IS_LOCAL_ENV, IS_STAGE_ENV } from '@admin-panel/config/env';
import type { HubAlertStatus } from '../hubAlerts/hubPushRegistration';
import {
  HUB_ALERT_SETUP_MODE,
  MODE_OVERRIDE_PARAM,
  resolveSetupMode,
  type HubAlertSetupMode,
} from './hubAlertSetupRules';

/**
 * Small shared state for alert setup, kept out of React so the gate, the strip, the setup page and
 * HubAlerts (which registers the device) agree without prop drilling. No Firebase here: this module
 * loads with every Hub page.
 */

/** Local/Stage preview mode, shared by every tab and the installed app window, kept across logout. */
export const MODE_OVERRIDE_KEY = 'hub-alert-setup-mode';
/** Local/Stage only: `?hubAlertSetupReset=1` forgets this device's sound test (walk setup again). */
export const RESET_PARAM = 'hubAlertSetupReset';

const previewAllowed = () => IS_LOCAL_ENV || IS_STAGE_ENV;

/**
 * Local/Stage preview controls, read once at boot (main.tsx): `?hubAlertSetupMode=off|soft|hard`
 * (or `=default` to drop the preview) and `?hubAlertSetupReset=1`. Production ignores both.
 */
export const applyPreviewParams = () => {
  if (!previewAllowed()) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get(MODE_OVERRIDE_PARAM);
    if (mode === 'default') localStorage.removeItem(MODE_OVERRIDE_KEY);
    else if (mode) localStorage.setItem(MODE_OVERRIDE_KEY, mode);
    if (params.get(RESET_PARAM) === '1') localStorage.removeItem(SETUP_DONE_KEY);
  } catch {
    // Storage blocked: the configured mode applies.
  }
};

/** Rollout mode (the Local/Stage preview applies after a page load). */
export const currentSetupMode = (): HubAlertSetupMode => {
  let override: string | null = null;
  if (previewAllowed()) {
    try {
      override = localStorage.getItem(MODE_OVERRIDE_KEY);
    } catch {
      override = null;
    }
  }
  return resolveSetupMode(HUB_ALERT_SETUP_MODE, override, previewAllowed());
};

/** A device fact, kept across logout (see DEVICE_STORAGE_KEYS in libs/providers authSession). */
export const SETUP_DONE_KEY = 'hub-alert-setup-done';
const CHANGED_EVENT = 'hub-alert-setup-changed';

const notify = () => window.dispatchEvent(new Event(CHANGED_EVENT));

export const readTestPassed = () => {
  try {
    return Boolean(localStorage.getItem(SETUP_DONE_KEY));
  } catch {
    return false;
  }
};

/** When this device last passed the sound test, or null. */
export const readTestPassedAt = (): Date | null => {
  try {
    const value = localStorage.getItem(SETUP_DONE_KEY);
    const at = value ? new Date(value) : null;
    return at && !Number.isNaN(at.getTime()) ? at : null;
  } catch {
    return null;
  }
};

export const markTestPassed = () => {
  try {
    localStorage.setItem(SETUP_DONE_KEY, new Date().toISOString());
  } catch {
    // Storage blocked: setup asks again next time, never worse than a missed check.
  }
  notify();
};

/** Permission was withdrawn: the sound test no longer proves anything. */
export const clearTestPassed = () => {
  try {
    if (!localStorage.getItem(SETUP_DONE_KEY)) return;
    localStorage.removeItem(SETUP_DONE_KEY);
  } catch {
    return;
  }
  notify();
};

/** Latest device registration result from HubAlerts; null until its first attempt finishes. */
let registrationStatus: HubAlertStatus | null = null;
let pendingSince: number | null = null;
/** The result before the registration now running (a retry or re-bind), for the same operator. */
let previousResult: HubAlertStatus | null = null;

/** HubAlerts started (re)registering this device, for example right after a login. */
export const markRegistrationStarted = () => {
  previousResult = registrationStatus ?? previousResult;
  registrationStatus = null;
  pendingSince = Date.now();
  notify();
};

export const publishRegistrationStatus = (status: HubAlertStatus) => {
  pendingSince = null;
  if (registrationStatus === status) return;
  registrationStatus = status;
  notify();
};

export const getRegistrationStatus = () => registrationStatus;

/** While a registration runs, the result it will most likely repeat (null after a login). */
export const previousRegistrationResult = () => (pendingSince === null ? null : previousResult);

/** Logout: the next operator's registration result is unknown until HubAlerts registers again. */
export const resetRegistrationStatus = () => {
  registrationStatus = null;
  previousResult = null;
  pendingSince = null;
  notify();
};
/** When the running registration started; null when none is running. */
export const registrationPendingSince = () => pendingSince;

/**
 * The setup page asks HubAlerts to turn alerts on. Dispatched synchronously inside the button's
 * click handler, so the browser permission prompt still counts as coming from that tap.
 */
export const ENABLE_ALERTS_EVENT = 'hub-alert-enable';
export const requestEnableAlerts = () => window.dispatchEvent(new Event(ENABLE_ALERTS_EVENT));

/** Re-runs `listener` whenever setup state may have changed (this tab, another tab, permission). */
export const subscribeSetupChanges = (listener: () => void) => {
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === SETUP_DONE_KEY) listener();
  };
  window.addEventListener(CHANGED_EVENT, listener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGED_EVENT, listener);
    window.removeEventListener('storage', onStorage);
  };
};

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let installPrompt: InstallPromptEvent | null = null;
let justInstalled = false;

/**
 * Chrome offers its own install prompt only sometimes; when it does, keep it for the setup page's
 * Install button. Chrome's own install banner is left alone: it helps everyone install Hub. Called
 * once from main.tsx, before the event can fire.
 */
export const captureInstallPrompt = () => {
  window.addEventListener('beforeinstallprompt', event => {
    installPrompt = event as InstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installPrompt = null;
    justInstalled = true;
    notify();
  });
};

export const canPromptInstall = () => installPrompt !== null;
export const wasJustInstalled = () => justInstalled;

export const promptInstall = async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
  const pending = installPrompt;
  if (!pending) return 'unavailable';
  installPrompt = null;
  try {
    await pending.prompt();
    const { outcome } = await pending.userChoice;
    return outcome;
  } catch {
    // Already used by Chrome's own banner: the page shows the menu steps instead.
    return 'unavailable';
  } finally {
    notify();
  }
};
